/**
 * @file useOrderCustomerPicker.ts
 * @description Выбор клиента в форме заказа: inline-подбор и предзаполнение
 * из сделки (вынесено из `OrderCreatePage` в phase19.24 шаг 5.1).
 *
 * Отдельно от формы заказа: карточка клиента — самостоятельный кусок со
 * своим запросом, дебаунсом и состоянием «уже выбран». Форме важен только
 * итог — `customer`, а не три служебных поля.
 */

import { useEffect, useState } from "react";

import { customers, type CustomerDto } from "../lib/api-customers";

export interface OrderCustomerPickerState {
  customer: CustomerDto | null;
  query: string;
  options: CustomerDto[];
  setQuery: (value: string) => void;
  pick: (customer: CustomerDto) => void;
  clear: () => void;
}

/**
 * @param preCustomerId клиент из карточки сделки — подставляем один раз,
 * выбор менеджера он не перебивает.
 */
export function useOrderCustomerPicker(
  preCustomerId: string | null,
): OrderCustomerPickerState {
  const [customer, setCustomer] = useState<CustomerDto | null>(null);
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<CustomerDto[]>([]);

  useEffect(() => {
    if (!preCustomerId || customer) return;
    let cancelled = false;
    void customers
      .findOne(preCustomerId)
      .then((c) => {
        // Справочник не ответил — менеджер найдёт клиента руками, форма жива.
        if (!cancelled) setCustomer(c);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [preCustomerId, customer]);

  useEffect(() => {
    if (query.trim().length < 2 || customer) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(() => {
      void customers
        .list({ search: query.trim(), limit: 10 })
        .then((res) => setOptions(res.items))
        .catch(() => undefined);
    }, 250);
    return () => clearTimeout(timer);
  }, [query, customer]);

  return {
    customer,
    query,
    options,
    setQuery,
    pick: (next) => {
      setCustomer(next);
      setQuery("");
      setOptions([]);
    },
    clear: () => setCustomer(null),
  };
}
